import './rater.contents.scss';

import { Alert, Box, Slide, Tab, Tabs, Typography } from '@mui/material';
import { CheckCircle, PriorityHigh, Sync } from '@mui/icons-material';
import { ConvergeTooltip, DialogLayout } from '../../../presentation';
import { FormProvider, useForm } from 'react-hook-form';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import {
  QuoteStatus,
  QuoteType,
  SignificantTermsConditions,
  SubmissionType,
} from '@converge/graphql';
import { RaterTab, UNDERWRITING_RATER_TAB_NAMES } from './types';
import { capitalizeFirstLetter, displayDateFromNow } from '@converge/utils';
import {
  useRaterActionDispatchContext,
  useRaterIsEditable,
  useRaterIsExcessTabDisabled,
  useRaterStateContextSelector,
  useRaterUnsavedCounts,
} from './data';

import Circle from '@mui/icons-material/Circle';
import { RaterBoundPremium } from './enterprise-rating/rater.bound-premium';
import { RaterContentTable } from './rater.content.table';
import { RaterEnterpriseFusionScore } from './enterprise-rating';
import { RaterExcessView } from './excess-rating/rater.excess.view';
import { RaterFooter } from './footer/rater.footer';
import { RaterMode } from '@converge/graphql';
import { RaterQuoteName } from './header';
import { RaterScheduleRating } from './enterprise-rating';
import { RaterSignificantTermsAndConditions } from './enterprise-rating/rater.significant-terms-and-conditions';
import { Stack } from '@mui/system';
import config from '@converge/config';
import pluralize from 'pluralize';
import { useUnderwritingSubmissionDetailPage } from '../underwriting.submission.detail.hooks';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';
import {
  SR_MAX_ITEM_PERCENTAGE,
  SR_MAX_TOTAL_PERCENTAGE,
  SR_MIN_ITEM_PERCENTAGE,
  SR_MIN_TOTAL_PERCENTAGE,
} from './data/rater.constants';

const QuoteName = ({ children }: PropsWithChildren) => {
  return (
    <Typography fontWeight={'normal'} marginRight={1}>
      {children}
    </Typography>
  );
};

const SHOW_EXTRA_SECTIONS = false;
export const RaterContents = () => {
  const { navigate } = useUnderwritingSubmissionDetailPage();
  const { tab, transitioningAwayFromTab } = useRaterStateContextSelector(
    ({ tab, transitioningAwayFromTab }) => ({
      tab,
      transitioningAwayFromTab,
    })
  );

  const { actions, dispatch } = useRaterActionDispatchContext();

  const path = 'quote';

  const formMethods = useForm();
  return (
    <FormProvider {...formMethods}>
      <DialogLayout
        noHeaderPaddingY
        header={<RaterHeader />}
        open={true}
        maxWidth={false}
        fullWidth
        onCloseIconClick={() => {
          navigate.page({
            sectionUrl: path,
          });
        }}
        onClose={() => {
          navigate.page({
            sectionUrl: path,
          });
        }}
        showCloseButton
        grayFooter
        footer={<RaterFooter />}
        noPadding
        scrollContents
        TransitionComponent={Slide}
      >
        <>
          <Slide
            in={!tab || tab === RaterTab.CoverageRating}
            direction="right"
            appear={false}
            onTransitionEnd={() => dispatch(actions.finishTabTransition())}
          >
            <Stack className="rater-content-tab">
              {[tab, transitioningAwayFromTab].includes(
                RaterTab.CoverageRating
              ) && <RaterContentTable />}
            </Stack>
          </Slide>
          <Slide
            in={tab === RaterTab.EnterpriseRating}
            direction="left"
            onTransitionEnd={() => dispatch(actions.finishTabTransition())}
          >
            <Stack className="rater-content-tab">
              {[tab, transitioningAwayFromTab].includes(
                RaterTab.EnterpriseRating
              ) && (
                <Box minHeight={'100vh'}>
                  <EnterpriseRatingView />
                </Box>
              )}
            </Stack>
          </Slide>
          <Slide
            in={!tab || tab === RaterTab.ExcessRating}
            direction="right"
            appear={false}
            onTransitionEnd={() => dispatch(actions.finishTabTransition())}
          >
            <Stack className="rater-content-tab" flexGrow={1}>
              {[tab, transitioningAwayFromTab].includes(
                RaterTab.ExcessRating
              ) && <RaterExcessView />}
            </Stack>
          </Slide>
        </>
      </DialogLayout>
    </FormProvider>
  );
};

const RaterHeader = () => {
  const [showSavedQuoteIndicator, setShowSavedQuoteIndicator] = useState(false);
  const [showRatingUpdateIndicator, setShowRatingUpdateIndicator] =
    useState(false);
  const {
    mode,
    isObsolete,
    status,
    tab,
    transitioningAwayFromTab,
    quoteName,
    isRequestingRatingUpdate,
    lastReceivedMessageAt,
    raterError,
    isAuthenticated,
    upsertQuoteIsLoading,
    hasSavedQuote,
    authenticationStatusChangedAt,
  } = useRaterStateContextSelector(
    ({
      mode,
      quote: { type, name, status, isObsolete },
      userQuotes: { pageInfo },
      tab,
      transitioningAwayFromTab,
      isRequestingRatingUpdate,
      lastReceivedMessageAt,
      raterError,
      isAuthenticated,
      upsertQuoteIsLoading,
      hasSavedQuote,
      authenticationStatusChangedAt,
    }) => {
      return {
        quoteName:
          mode === RaterMode.CreateQuote && type === QuoteType.DefaultQuote
            ? `Option ${(pageInfo.totalResults ?? 0) + 1}`
            : type === QuoteType.DefaultQuote
            ? ''
            : name || '',
        mode,
        tab,
        transitioningAwayFromTab,
        status,
        isObsolete,
        isRequestingRatingUpdate,
        lastReceivedMessageAt,
        raterError,
        isAuthenticated,
        upsertQuoteIsLoading,
        hasSavedQuote,
        authenticationStatusChangedAt,
      };
    }
  );
  const isEditable = useRaterIsEditable();

  const { info: submissionInfo } = useUnderwritingSubmissionStateContext();
  const { actions, dispatch } = useRaterActionDispatchContext();

  const isExcessTabDisabled = useRaterIsExcessTabDisabled();

  const unsavedCounts = useRaterUnsavedCounts();

  let title: string;
  switch (mode) {
    case RaterMode.CreateQuote:
      title = `Create ${capitalizeFirstLetter(
        submissionInfo?.policyTypeName
      )} Quote Option`;
      break;
    case RaterMode.EditQuote:
      title = `Edit ${capitalizeFirstLetter(
        submissionInfo?.policyTypeName
      )} Quote Option`;
      break;
    default:
      title = 'Coverage & Rating';
  }

  useEffect(() => {
    if (hasSavedQuote) {
      setShowSavedQuoteIndicator(true);
      const timeout = setTimeout(() => {
        setShowSavedQuoteIndicator(false);
      }, 4 * 1000);
      return () => {
        setShowSavedQuoteIndicator(false);
        clearTimeout(timeout);
      };
    }
    setShowSavedQuoteIndicator(false);
    return () => {
      setShowSavedQuoteIndicator(false);
    };
  }, [hasSavedQuote]);

  useEffect(() => {
    if (raterError) {
      dispatch(actions.setIsInvalidRaterData(true));
    }
    setShowRatingUpdateIndicator(false);

    if (lastReceivedMessageAt) {
      setShowRatingUpdateIndicator(true);
      const timeout = setTimeout(() => {
        setShowRatingUpdateIndicator(false);
      }, 5 * 1000);
      return () => {
        setShowRatingUpdateIndicator(false);
        clearTimeout(timeout);
      };
    }

    setShowRatingUpdateIndicator(false);

    return () => {
      setShowRatingUpdateIndicator(false);
    };
  }, [
    actions,
    dispatch,
    lastReceivedMessageAt,
    raterError,
    setShowRatingUpdateIndicator,
  ]);

  const raterIndicatorTooltip = useMemo(() => {
    const raterIndicatorTooltip: string[] = [];

    if (!config.featureFlags.enableRaterStateIndicator) {
      return 'This feature is disabled.';
    }

    if (isRequestingRatingUpdate) {
      raterIndicatorTooltip.push('Rating in progress...');
    }

    if (upsertQuoteIsLoading) {
      raterIndicatorTooltip.push('Saving quote...');
    }

    if (lastReceivedMessageAt) {
      const date = displayDateFromNow(lastReceivedMessageAt.toISOString(), {
        showTime: true,
      });
      if (raterError) {
        raterIndicatorTooltip.push(`Rating failed ${date}.`);
      } else if (!isRequestingRatingUpdate) {
        raterIndicatorTooltip.push(`Rated ${date}.`);
      }
    } else if (isAuthenticated) {
      if (authenticationStatusChangedAt) {
        const connectedAtDate = displayDateFromNow(
          authenticationStatusChangedAt.toISOString(),
          {
            showTime: true,
          }
        );
        raterIndicatorTooltip.push(`Connected to rater ${connectedAtDate}.`);
      } else {
        raterIndicatorTooltip.push(`Connected to rater.`);
      }
    }

    if (!isAuthenticated && authenticationStatusChangedAt) {
      const disconnectedAtDate = displayDateFromNow(
        authenticationStatusChangedAt.toISOString(),
        {
          showTime: true,
        }
      );
      raterIndicatorTooltip.push(
        `Rater disconnected at ${disconnectedAtDate}.`
      );
    } else if (!isAuthenticated && raterError) {
      raterIndicatorTooltip.push('Rater was unable to connect.');
    } else if (!isAuthenticated) {
      raterIndicatorTooltip.push('Rater is not connected.');
    }

    if (isEditable) {
      raterIndicatorTooltip.push('Click to manually request a new rating.');
    }

    return raterIndicatorTooltip.join(' | ');
  }, [
    authenticationStatusChangedAt,
    isAuthenticated,
    isEditable,
    isRequestingRatingUpdate,
    lastReceivedMessageAt,
    raterError,
    upsertQuoteIsLoading,
  ]);

  return (
    <Stack className="rater-header">
      <Stack className="title-and-option-container">
        {config.featureFlags.enableRaterStateIndicator && (
          <Box>
            <ConvergeTooltip title={raterIndicatorTooltip}>
              <Box>
                {showSavedQuoteIndicator ? (
                  <CheckCircle color="success" className="check-circle " />
                ) : upsertQuoteIsLoading ? (
                  <Sync className="spin sync-style" />
                ) : (
                  <Circle
                    sx={{
                      mx: '3px',
                      fontSize: 16,
                      cursor: isEditable ? 'pointer' : undefined,
                      color:
                        raterError ||
                        (!isAuthenticated && authenticationStatusChangedAt)
                          ? '#db524c'
                          : showRatingUpdateIndicator
                          ? '#59b959'
                          : isAuthenticated
                          ? '#3d8bcd'
                          : lastReceivedMessageAt
                          ? '#f2ae43'
                          : '#ededed',
                    }}
                    className={`transition-color transition-opacity ${
                      isRequestingRatingUpdate ? 'pulsate' : ''
                    }`}
                    onClick={() => {
                      if (isEditable) {
                        dispatch(
                          actions.requestRatingUpdate('manualUserRequest')
                        );
                      }
                    }}
                  />
                )}
              </Box>
            </ConvergeTooltip>
          </Box>
        )}

        <Box className="quote-name">
          <Typography fontWeight={'bold'} marginRight={1}>
            {title}
          </Typography>
          <Typography fontSize={'small'} color="gray" marginRight={1}>
            {submissionInfo.insuredEntityName}
          </Typography>
        </Box>
        <Box className="quote-option ">
          <RaterQuoteName
            quoteName={quoteName}
            onChange={(quoteName) => {
              dispatch(actions.setQuoteName(quoteName));
            }}
            disabled={
              !submissionInfo.allowSelectingQuotesForQuotePackage ||
              isObsolete ||
              status
                ? status !== null && status !== undefined
                  ? [QuoteStatus.Quoted, QuoteStatus.Bound].includes(status)
                  : false
                : false
            }
            component={QuoteName}
          />
        </Box>
      </Stack>
      <Box className="tabs-container">
        <Tabs
          style={{ color: 'red' }}
          value={tab}
          onChange={(_, tab: RaterTab) => {
            dispatch(actions.setTab(tab));
          }}
          centered
        >
          <Tab
            label={
              submissionInfo.submission?.type === SubmissionType.Excess
                ? 'Primary Rating'
                : UNDERWRITING_RATER_TAB_NAMES[RaterTab.CoverageRating]
            }
            value={RaterTab.CoverageRating}
            sx={{
              opacity:
                transitioningAwayFromTab === RaterTab.CoverageRating
                  ? 0.5
                  : undefined,
              textTransform: 'none',
            }}
          />
          <Tab
            label={UNDERWRITING_RATER_TAB_NAMES[RaterTab.EnterpriseRating]}
            value={RaterTab.EnterpriseRating}
            sx={{
              opacity:
                transitioningAwayFromTab === RaterTab.EnterpriseRating
                  ? 0.5
                  : undefined,
              textTransform: 'none',
            }}
          />
          {submissionInfo.submission?.type === SubmissionType.Excess && (
            <Tab
              title={
                isExcessTabDisabled
                  ? `Save quote before proceeding (${
                      unsavedCounts.total
                    } unsaved line ${pluralize('item', unsavedCounts.total)})`
                  : ''
              }
              label={
                isExcessTabDisabled
                  ? `${
                      UNDERWRITING_RATER_TAB_NAMES[RaterTab.ExcessRating]
                    } (save first)`
                  : UNDERWRITING_RATER_TAB_NAMES[RaterTab.ExcessRating]
              }
              value={RaterTab.ExcessRating}
              disabled={isExcessTabDisabled}
              sx={{
                opacity:
                  transitioningAwayFromTab === RaterTab.ExcessRating
                    ? 0.5
                    : undefined,
                textTransform: 'none',
              }}
            />
          )}
        </Tabs>
      </Box>

      <Stack
        sx={{ paddingTop: `${raterError ? '10px' : '0px'}` }}
        overflow={'hidden'}
      >
        <Slide appear in={!!raterError} unmountOnExit>
          <Alert
            color="error"
            onClose={() => {
              dispatch(actions.clearRaterError());
            }}
            icon={<PriorityHigh />}
          >
            {raterError}
          </Alert>
        </Slide>
      </Stack>
    </Stack>
  );
};

const EnterpriseRatingView = () => {
  const {
    scheduleRatings,
    stcMaxModifier,
    stcMinModifier,
    stcRatedModifier,
    stcSelectedModifier,
    endorsementImpact,
    srModifier,
    srMinPercentage,
    srMaxPercentage,
    srMinItemPercentage,
    srMaxItemPercentage,
    hasStcSelectedModifierChanged,
    hasEndorsementImpactChanged,
  } = useRaterStateContextSelector(
    ({
      quote: {
        scheduleRatings,
        stcMaxModifier,
        stcMinModifier,
        stcRatedModifier,
        stcSelectedModifier,
        endorsementImpact,
        srModifier,
        srMinPercentage,
        srMaxPercentage,
        srMinItemPercentage,
        srMaxItemPercentage,
        hasStcSelectedModifierChanged,
        hasEndorsementImpactChanged,
      },
    }) => {
      return {
        scheduleRatings,
        stcMaxModifier,
        stcMinModifier,
        stcRatedModifier,
        stcSelectedModifier,
        endorsementImpact,
        srModifier,
        srMinPercentage,
        srMaxPercentage,
        srMinItemPercentage,
        srMaxItemPercentage,
        hasStcSelectedModifierChanged,
        hasEndorsementImpactChanged,
      };
    }
  );

  const { actions, dispatch } = useRaterActionDispatchContext();

  return (
    <>
      <Stack className="enterprise-rating-main-container">
        <Stack flexGrow={1} padding={1}>
          <RaterEnterpriseFusionScore />
        </Stack>
        <Stack flexGrow={1} padding={1}>
          <RaterScheduleRating
            scheduleRatings={scheduleRatings.filter(
              (scheduleRating) => scheduleRating.primary
            )}
            modifier={srModifier}
            minPercentage={srMinItemPercentage ?? SR_MIN_ITEM_PERCENTAGE}
            maxPercentage={srMaxItemPercentage ?? SR_MAX_ITEM_PERCENTAGE}
            minTotalPercentage={srMinPercentage ?? SR_MIN_TOTAL_PERCENTAGE}
            maxTotalPercentage={srMaxPercentage ?? SR_MAX_TOTAL_PERCENTAGE}
            onChange={(uuid, value) => {
              dispatch(actions.setScheduleRatingPercentage(uuid, value ?? 0));
            }}
          />
          <Stack paddingTop={2}>
            <RaterSignificantTermsAndConditions
              onChangeEndorsementImpact={(endorsementImpact) => {
                dispatch(actions.setEndorsementImpact(endorsementImpact));
              }}
              onChangeStcSelectedModifier={(stcSelectedModifier) => {
                dispatch(
                  actions.setQuoteField(
                    'stcSelectedModifier',
                    stcSelectedModifier
                  )
                );
              }}
              endorsementImpact={
                endorsementImpact ?? SignificantTermsConditions.Neutral
              }
              isPrimary
              stcMaxModifier={stcMaxModifier}
              stcMinModifier={stcMinModifier}
              stcRatedModifier={stcRatedModifier}
              stcSelectedModifier={stcSelectedModifier}
              hasEndorsementImpactChanged={hasEndorsementImpactChanged}
              hasStcSelectedModifierChanged={hasStcSelectedModifierChanged}
            />
          </Stack>
        </Stack>
      </Stack>
      {SHOW_EXTRA_SECTIONS && (
        <>
          <Stack flexDirection={'column'} marginY={2} flexGrow={1}>
            <RaterBoundPremium />
          </Stack>
        </>
      )}
    </>
  );
};
